import { zodResolver } from "@hookform/resolvers/zod";
import { AlertCircle, LoaderCircle, Save } from "lucide-react";
import { type FocusEventHandler, useMemo, useRef, useState } from "react";
import { type FieldPath, type UseFormSetError, useForm } from "react-hook-form";
import { z } from "zod";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { FieldError } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ApiError } from "@/lib/api";

// Modo del formulario. Desde #239 el alta NO pide contraseña: da de alta = invitar, así
// que la clave la elige su titular al canjear el enlace del correo. Lo que el alta sí
// exige es el email, que es por donde viaja la invitación.
export type UsuarioFormMode = "crear" | "editar";

// Criterio «tiene forma de email» compartido por la validación del campo email y el
// prefill del alias en el alta (#193): un alias con esta forma se copia al email.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Espejo de la validación de servidor (usermgmt/dto.go). Los campos opcionales se
// validan como "vacío o válido"; el backend es el guardián real. F7 (#153): la cuenta
// es global y SIN rol ni autorización LOTO (ambos son por obra); solo lleva el flag
// `es_admin` (superadministrador global).
const personaSchema = z.object({
  nombre: z.string().trim().min(1, "El nombre es obligatorio").max(120, "Máximo 120 caracteres"),
  apellidos: z
    .string()
    .trim()
    .min(1, "Los apellidos son obligatorios")
    .max(120, "Máximo 120 caracteres"),
  dni_o_matricula: z.string().trim().max(120, "Máximo 120 caracteres"),
  email: z
    .string()
    .trim()
    .max(254, "Máximo 254 caracteres")
    .refine((v) => v === "" || EMAIL_RE.test(v), "El email no tiene un formato válido"),
  cargo: z.string().trim().max(120, "Máximo 120 caracteres"),
  area: z.string().trim().max(120, "Máximo 120 caracteres"),
});

function makeSchema(mode: UsuarioFormMode) {
  return z
    .object({
      persona: personaSchema,
      nombre_usuario: z
        .string()
        .trim()
        .min(3, "El nombre de usuario debe tener al menos 3 caracteres")
        .max(120, "Máximo 120 caracteres"),
      es_admin: z.boolean(),
      activo: z.boolean(),
    })
    .superRefine((val, ctx) => {
      // #239 R1: en el ALTA el correo es obligatorio (es donde se envía la invitación);
      // al editar sigue siendo opcional, porque hay fichas antiguas sin él.
      if (mode === "crear" && val.persona.email.trim() === "") {
        ctx.addIssue({
          path: ["persona", "email"],
          code: z.ZodIssueCode.custom,
          message: "El email es obligatorio",
        });
      }
    });
}

export type UsuarioFormValues = z.infer<ReturnType<typeof makeSchema>>;

// Claves de error por campo del backend que sabemos mapear al formulario (mismas
// rutas con notación de punto que react-hook-form). Cubre 422 VALIDACION y el 409
// de nombre_usuario duplicado, que también viaja con `fields`.
const CAMPOS_SERVIDOR = [
  "persona.nombre",
  "persona.apellidos",
  "persona.email",
  "persona.dni_o_matricula",
  "persona.cargo",
  "persona.area",
  "nombre_usuario",
] as const satisfies readonly FieldPath<UsuarioFormValues>[];

function aplicarError(
  err: unknown,
  setError: UseFormSetError<UsuarioFormValues>,
  setFormError: (msg: string) => void,
): void {
  if (err instanceof ApiError) {
    if (err.fields) {
      let mapped = false;
      for (const campo of CAMPOS_SERVIDOR) {
        const msg = err.fields[campo];
        if (msg) {
          setError(campo, { message: msg });
          mapped = true;
        }
      }
      if (mapped) {
        return;
      }
    }
    setFormError(err.message || "No se pudo guardar el usuario.");
    return;
  }
  setFormError("No se pudo guardar el usuario. Comprueba tu conexión.");
}

/**
 * UsuarioForm es el formulario de alta/edición de persona + usuario de sistema:
 * datos personales, cuenta (nombre de usuario), flag de administrador global y estado
 * activo. Desde #239 el alta no fija contraseña —envía una invitación por correo—. F7 (#153): el rol y la autorización LOTO
 * NO viven aquí (son por obra, gestionados desde la obra). La identidad Microsoft no
 * se edita a mano: se auto-vincula por email en el login SSO (#190). Mapea los errores
 * por campo del backend (VALIDACION / duplicados). Sigue el patrón de EquipoForm (#34).
 */
export function UsuarioForm({
  mode,
  defaultValues,
  submitLabel,
  pending,
  onSubmit,
  onCancel,
}: {
  mode: UsuarioFormMode;
  defaultValues: UsuarioFormValues;
  submitLabel: string;
  pending: boolean;
  onSubmit: (values: UsuarioFormValues) => Promise<void>;
  onCancel: () => void;
}) {
  const [formError, setFormError] = useState<string | null>(null);
  const schema = useMemo(() => makeSchema(mode), [mode]);
  const {
    register,
    handleSubmit,
    setError,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<UsuarioFormValues>({ resolver: zodResolver(schema), defaultValues });

  // Prefill del email a partir del alias en el ALTA (#193): si el usuario teclea un
  // nombre de usuario con forma de email y el campo email sigue vacío, se copia al
  // email (editable). `prefilledFrom` recuerda el último alias que copiamos para no
  // volver a rellenarlo si el usuario lo borra a propósito; solo reaparece si el alias
  // cambia. En edición no se toca nada.
  const nombreUsuario = register("nombre_usuario");
  const prefilledFrom = useRef<string | null>(null);
  const prefillEmailDesdeAlias: FocusEventHandler<HTMLInputElement> = (e) => {
    nombreUsuario.onBlur(e);
    if (mode !== "crear") return;
    const alias = getValues("nombre_usuario").trim();
    if (alias === "" || alias === prefilledFrom.current || !EMAIL_RE.test(alias)) return;
    if (getValues("persona.email").trim() !== "") return;
    setValue("persona.email", alias, { shouldDirty: true });
    prefilledFrom.current = alias;
  };

  const submit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      await onSubmit(values);
    } catch (err) {
      aplicarError(err, setError, setFormError);
    }
  });

  return (
    <form className="flex flex-col gap-5" onSubmit={submit} noValidate>
      {formError ? (
        <Alert variant="destructive">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          <span>{formError}</span>
        </Alert>
      ) : null}

      {/* --- Datos personales --- */}
      <fieldset className="flex flex-col gap-4">
        <legend className="font-semibold text-slate-900 text-sm">Datos personales</legend>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="persona.nombre">Nombre</Label>
            <Input
              id="persona.nombre"
              autoComplete="off"
              aria-invalid={errors.persona?.nombre ? true : undefined}
              {...register("persona.nombre")}
            />
            <FieldError>{errors.persona?.nombre?.message}</FieldError>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="persona.apellidos">Apellidos</Label>
            <Input
              id="persona.apellidos"
              autoComplete="off"
              aria-invalid={errors.persona?.apellidos ? true : undefined}
              {...register("persona.apellidos")}
            />
            <FieldError>{errors.persona?.apellidos?.message}</FieldError>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="persona.dni_o_matricula">DNI o matrícula (opcional)</Label>
            <Input
              id="persona.dni_o_matricula"
              autoComplete="off"
              aria-invalid={errors.persona?.dni_o_matricula ? true : undefined}
              {...register("persona.dni_o_matricula")}
            />
            <FieldError>{errors.persona?.dni_o_matricula?.message}</FieldError>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="persona.email">{mode === "crear" ? "Email" : "Email (opcional)"}</Label>
            <Input
              id="persona.email"
              type="email"
              autoComplete="off"
              aria-invalid={errors.persona?.email ? true : undefined}
              {...register("persona.email")}
            />
            <FieldError>{errors.persona?.email?.message}</FieldError>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="persona.cargo">Cargo (opcional)</Label>
            <Input
              id="persona.cargo"
              autoComplete="off"
              aria-invalid={errors.persona?.cargo ? true : undefined}
              {...register("persona.cargo")}
            />
            <FieldError>{errors.persona?.cargo?.message}</FieldError>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="persona.area">Área (opcional)</Label>
            <Input
              id="persona.area"
              autoComplete="off"
              aria-invalid={errors.persona?.area ? true : undefined}
              {...register("persona.area")}
            />
            <FieldError>{errors.persona?.area?.message}</FieldError>
          </div>
        </div>
      </fieldset>

      {/* --- Cuenta --- */}
      <fieldset className="flex flex-col gap-4">
        <legend className="font-semibold text-slate-900 text-sm">Cuenta</legend>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="nombre_usuario">Nombre de usuario</Label>
          <Input
            id="nombre_usuario"
            autoComplete="off"
            aria-invalid={errors.nombre_usuario ? true : undefined}
            {...nombreUsuario}
            onBlur={prefillEmailDesdeAlias}
          />
          <FieldError>{errors.nombre_usuario?.message}</FieldError>
        </div>

        {mode === "crear" ? (
          <p className="rounded-lg bg-slate-50 px-4 py-3 text-slate-600 text-sm">
            No se fija ninguna contraseña: la persona recibirá un correo con un enlace de un solo
            uso (válido 7 días) para elegir la suya y entrar.
          </p>
        ) : null}

        <label className="flex items-center gap-3">
          <input
            type="checkbox"
            className="h-5 w-5 shrink-0 rounded border-slate-300 text-primary-600 focus-visible:ring-2 focus-visible:ring-primary-500"
            {...register("es_admin")}
          />
          <span className="text-base text-slate-800">
            Administrador global (gestiona obras y usuarios)
          </span>
        </label>

        <label className="flex items-center gap-3">
          <input
            type="checkbox"
            className="h-5 w-5 shrink-0 rounded border-slate-300 text-primary-600 focus-visible:ring-2 focus-visible:ring-primary-500"
            {...register("activo")}
          />
          <span className="text-base text-slate-800">Cuenta activa</span>
        </label>
      </fieldset>

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={pending}>
          Cancelar
        </Button>
        <Button type="submit" disabled={pending}>
          {pending ? (
            <LoaderCircle className="h-5 w-5 animate-spin" aria-hidden="true" />
          ) : (
            <Save className="h-5 w-5" aria-hidden="true" />
          )}
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}
